import { useEffect } from 'react';
import { Review, AggregateRatingData } from './useReviews';
import { injectJsonLd, removeJsonLd } from '../lib/schemaBuilder';
import { CORE_BUSINESS, SCHEMA_IDS, isValidAggregateRating } from '../lib/coreSchemas';

/**
 * Injects Schema.org structured data for the Reviews page.
 * ✅ CRITICAL: AggregateRating and Review snippets are ONLY added when real
 * approved reviews exist — never with fake/mock data.
 * 
 * This ensures compliance with Google's review snippet guidelines and prevents
 * penalties for fake/self-serving reviews.
 */
export function useReviewsPageSchema(
  reviews: Review[],
  aggregate: AggregateRatingData
) {
  useEffect(() => {
    // ✅ Validate that we have real reviews before injecting rating schemas
    const hasRealReviews = isValidAggregateRating({
      ratingValue: aggregate.ratingValue,
      reviewCount: aggregate.reviewCount
    });

    // ── LocalBusiness schema with aggregateRating (only when real reviews exist) ──
    const localBusinessSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'AutomotiveBusiness',
      '@id': SCHEMA_IDS.automotiveBusiness,
      name: CORE_BUSINESS.name,
      url: CORE_BUSINESS.url,
      telephone: CORE_BUSINESS.telephone,
      logo: {
        '@type': 'ImageObject',
        url: CORE_BUSINESS.logo,
        width: 400,
        height: 120
      },
      image: CORE_BUSINESS.logo,
      address: {
        '@type': 'PostalAddress',
        streetAddress: CORE_BUSINESS.address.streetAddress,
        addressLocality: CORE_BUSINESS.address.addressLocality,
        addressRegion: CORE_BUSINESS.address.addressRegion,
        postalCode: CORE_BUSINESS.address.postalCode,
        addressCountry: CORE_BUSINESS.address.addressCountry,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: CORE_BUSINESS.geo.latitude,
        longitude: CORE_BUSINESS.geo.longitude
      },
    };

    // ✅ Only attach aggregateRating when there are real approved reviews
    if (hasRealReviews) {
      localBusinessSchema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: String(aggregate.ratingValue),
        reviewCount: aggregate.reviewCount,
        bestRating: '5',
        worstRating: '1',
      };

      // ✅ Only attach individual review snippets when real reviews exist
      if (reviews.length > 0) {
        localBusinessSchema.review = reviews.slice(0, 10).map((r) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: r.name,
          },
          datePublished: new Date(r.created_at).toISOString().split('T')[0],
          reviewBody: r.text,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
          itemReviewed: {
            '@type': 'AutomotiveBusiness',
            '@id': SCHEMA_IDS.automotiveBusiness,
          },
          publisher: {
            '@type': 'Organization',
            '@id': SCHEMA_IDS.organization,
            name: CORE_BUSINESS.name,
            url: CORE_BUSINESS.url,
          },
        }));
      }
    }

    // ── BreadcrumbList schema ──
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${CORE_BUSINESS.url}/reviews#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: CORE_BUSINESS.url,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Reviews',
          item: `${CORE_BUSINESS.url}/reviews`,
        },
      ],
    };

    // ── WebPage schema ──
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${CORE_BUSINESS.url}/reviews#webpage`,
      name: 'Customer Reviews - Rockhound Auto',
      description: 'Read verified customer reviews and ratings for Rockhound Auto in Warrenton, Oregon. See what real customers say about our used car sales, auto broker services, and transparent pricing.',
      url: `${CORE_BUSINESS.url}/reviews`,
      isPartOf: { '@id': SCHEMA_IDS.website },
      about: { '@id': SCHEMA_IDS.automotiveBusiness },
      publisher: { '@id': SCHEMA_IDS.organization },
      breadcrumb: { '@id': `${CORE_BUSINESS.url}/reviews#breadcrumb` },
      mainEntity: { '@id': SCHEMA_IDS.automotiveBusiness },
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.review-card', '.review-stats', '.speakable-content'],
      },
      contactPoint: { '@id': SCHEMA_IDS.contactPoint },
      keywords: 'Rockhound Auto reviews, customer testimonials Warrenton Oregon, car dealer reviews Oregon Coast, auto broker ratings Pacific Northwest',
      inLanguage: 'en-US',
      dateModified: new Date().toISOString().split('T')[0],
    };

    injectJsonLd('schema-reviews-local-business', localBusinessSchema);
    injectJsonLd('schema-reviews-breadcrumb', breadcrumbSchema);
    injectJsonLd('schema-reviews-webpage', webPageSchema);

    // ── Standalone AggregateRating on the Reviews page entity ──
    // Injected separately so Google can surface star ratings for /reviews in SERPs
    // without conflating it with the LocalBusiness entity signal.
    if (hasRealReviews) {
      const reviewsPageRatingSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${CORE_BUSINESS.url}/reviews#reviews-aggregate`,
        url: `${CORE_BUSINESS.url}/reviews`,
        name: 'Customer Reviews — Rockhound Auto Sales Warrenton Oregon',
        description: 'Verified customer reviews and star ratings for Rockhound Auto Sales in Warrenton, Oregon. Real testimonials from used car buyers on the Oregon Coast and SW Washington.',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: String(aggregate.ratingValue),
          reviewCount: aggregate.reviewCount,
          bestRating: '5',
          worstRating: '1',
        },
        itemReviewed: {
          '@type': 'AutoDealer',
          '@id': SCHEMA_IDS.automotiveBusiness,
          name: CORE_BUSINESS.name,
          url: CORE_BUSINESS.url,
        },
      };
      injectJsonLd('schema-reviews-page-aggregate', reviewsPageRatingSchema);
    } else {
      removeJsonLd('schema-reviews-page-aggregate');
    }

    return () => {
      removeJsonLd('schema-reviews-local-business');
      removeJsonLd('schema-reviews-breadcrumb');
      removeJsonLd('schema-reviews-webpage');
      removeJsonLd('schema-reviews-page-aggregate');
    };
  }, [reviews, aggregate]);
}